import { useState, useEffect } from "react";
import axios from "axios";
import { Container, Row, Col, Image, Badge, OverlayTrigger, Tooltip, Form, InputGroup, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { buildUrlWithParams } from './utils.js';
import { FaUser, FaQuestionCircle, FaStar, FaTimes } from 'react-icons/fa';
import moment from 'moment';
import './ContestList.css';

// Helper to highlight plain text (for titles)
const highlightPlainText = (text, query) => {
  if (!query || !text) return text;
  // Split text into array of parts, separating out the matched query
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));
  
  return parts.map((part, i) => 
    part.toLowerCase() === query.toLowerCase() 
      ? <mark key={i} style={{ padding: 0, backgroundColor: '#ffea00' }}>{part}</mark> 
      : part
  );
};

// Helper to highlight HTML string safely (for descriptions)
const highlightHTMLText = (htmlContent, query) => {
  if (!query || !htmlContent) return htmlContent;
  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  
  // This regex matches the query ONLY if it is not inside an HTML tag.
  // It looks ahead and asserts that we don't see a closing '>' before an opening '<'.
  const regex = new RegExp(`(?![^<]*>)(${escapedQuery})`, 'gi');
  
  return htmlContent.replace(regex, '<mark style="padding: 0; background-color: #ffea00; color: inherit;">$1</mark>');
};

export default function ContestList() {
  const [contests, setContests] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsRange, setStatsRange] = useState('allTime'); // 'allTime' or 'pastMonth'
  const [searchQuery, setSearchQuery] = useState('');
  
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  useEffect(() => {
    fetchContests();
    fetchStats(statsRange);
  }, [statsRange]);

  const fetchContests = async () => {
    try {
      const response = await axios.get("/api/contest/get_past_contests.php");
      setContests(response.data);
    } catch (error) {
      console.error("Error fetching contests:", error);
    }
  };

  const fetchStats = async (range) => {
    try {
      const endpoint = range === 'allTime' ? "/api/contest/get_stats.php" : "/api/contest/get_monthly_stats.php";
      const response = await axios.get(endpoint);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const now = Date.now() / 1000;
  
  const currentContests = contests.filter(
    (contest) => contest.start_date <= now && contest.end_date > now
  );

  const allPastContests = contests.filter((contest) => contest.end_date <= now);

  // Generate available years based on start_date
  const availableYears = Array.from(
    new Set(allPastContests.map((c) => moment.unix(c.start_date).year()))
  ).sort((a, b) => b - a);

  // Ensure current year is always in the list even if there are no past contests yet
  if (!availableYears.includes(currentYear)) {
    availableYears.unshift(currentYear);
    availableYears.sort((a, b) => b - a);
  }

  // Filter past contests based on end_date, search query, AND start_date year selection
  const pastContests = allPastContests
    .filter((contest) => {
      if (searchQuery) {
        // If there's a search query, ignore the year selector and search ALL past contests
        const query = searchQuery.toLowerCase();
        const titleMatch = contest.title?.toLowerCase().includes(query);
        
        // Strip HTML tags from description so we don't accidentally search markup 
        const plainDesc = contest.short ? contest.short.replace(/<[^>]+>/g, '') : '';
        const descMatch = plainDesc.toLowerCase().includes(query);
        
        return titleMatch || descMatch;
      }
      
      // If no search query, filter by selected year (using start_date)
      return moment.unix(contest.start_date).year() === selectedYear;
    })
    .sort((a, b) => b.end_date - a.end_date);

  return (
    <Container className="mt-4">
      {currentContests.length > 0 && (
        <Row className="justify-content-center">
          <h2 className="text-center mb-5">Current Contests</h2>
          {currentContests.map(currentContest => (
            <Col lg={10} key={currentContest.id}>
              <Link to={`/gallery/contests/${currentContest.id}/`} style={{ textDecoration: 'none', color: 'inherit' }}>
                <div className="row align-items-center mb-0">
                  <Col md={2} className="text-center">
                    <img
                      src={
                        currentContest.image_url ||
                        `https://www.hearthcards.net/images/contest/sketch/${currentContest.id}.svg`
                      }
                      alt="Contest"
                      className="img-fluid mb-md-0 mb-2 mt-4 mt-md-1"
                      style={{ maxHeight: "100%", padding: "5px" }}
                    />
                  </Col>
                  <Col md={9}>
                    <div className="text-center text-md-start mb-1">
                      <h2 className="d-inline">{currentContest.title}</h2>
                      {currentContest.idea && (
                        <small className="text-muted ms-2">
                          Idea by{" "}
                          <Link
                            to={`/gallery/user/${currentContest.idea.id}`}
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            {currentContest.idea.username}
                          </Link>
                        </small>
                      )}
                    </div>
                    <div
                      className="text-muted small d-flex justify-content-center align-items-center justify-content-md-start align-items-md-start flex-wrap mb-2"
                      style={{ gap: '0.5rem', lineHeight: '1.2' }}
                    >
                      <span className="d-flex align-items-center" title={`${currentContest.submission_count} submissions`}>
                        <FaUser className="me-1" />
                        {currentContest.submission_count}
                      </span>
                      <span className="vr"></span>
                      <span className="d-flex align-items-center">
                        {moment.unix(currentContest.start_date).format("MMM D, YYYY")} -{" "}
                        {moment.unix(currentContest.end_date).format("MMM D, YYYY")}
                      </span>
                    </div>
                    <p
                      className="lead text-center text-md-start"
                      dangerouslySetInnerHTML={{ __html: currentContest.short }}
                    ></p>
                  </Col>
                </div>
              </Link>
            </Col>
          ))}
        </Row>
      )}

      <Row className="justify-content-center">
        <Col lg={10}>
          <div className="custom-separator my-5">
            <div className="separator-end"></div>
            <div className="separator-middle"></div>
            <div className="separator-end right"></div>
          </div>
          <h2 className="text-center mb-4">Past Contests</h2>

          {/* Search Bar */}
          <Row className="justify-content-center mb-3">
            <Col xs={12} md={8} lg={6}>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder="Search by title or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                  >
                    <FaTimes />
                  </Button>
                )}
              </InputGroup>
            </Col>
          </Row>

          {/* Year Selector */}
          <Row className="justify-content-center mb-5">
            <Col xs={12}>
              <div className="d-flex justify-content-center flex-wrap" style={{ gap: '15px', fontSize: '1.1rem' }}>
                {availableYears.map((year) => {
                  // Only show a year as 'selected' if there is no active search query
                  const isSelected = !searchQuery && selectedYear === year;
                  
                  return (
                    <span
                      key={year}
                      onClick={() => {
                        setSelectedYear(year);
                        // If they click a year while searching, clear the search so they can see that year
                        if (searchQuery) setSearchQuery('');
                      }}
                      className={isSelected ? 'text-primary fw-bold' : 'text-muted'}
                      style={{ cursor: 'pointer', userSelect: 'none', transition: 'color 0.2s' }}
                    >
                      {year}
                    </span>
                  );
                })}
              </div>
            </Col>
          </Row>

          <Row
            className="mb-4 text-center past-contest-card-row"
            xs={2}
            md={4}
            lg={5}
          >
            {pastContests.length > 0 ? (
              pastContests.map((contest) => (
                <Col
                  key={contest.id}
                  className="mb-4 text-center past-contest-card"
                >
                  <Link to={`/gallery/contests/${contest.id}/`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div className="ratio ratio-1x1 mb-2">
                      <img
                        src={
                          contest.image_url ||
                          `https://www.hearthcards.net/images/contest/sketch/${contest.id}.svg`
                        }
                        alt={contest.title}
                        className="img-fluid"
                        style={{ objectFit: 'cover' }}
                      />
                    </div>
                    <div style={{ fontSize: "1rem", wordWrap: 'break-word' }} className="mb-1">
                      {highlightPlainText(contest.title, searchQuery)}
                    </div>
                    <div
                      className="text-muted small d-flex justify-content-center align-items-center flex-wrap"
                      style={{ gap: '0.5rem', lineHeight: '1.2' }}
                    >
                      <span className="d-flex align-items-center" title={`${contest.submission_count} submissions`}>
                        <FaUser className="me-1" />
                        {contest.submission_count}
                      </span>
                      <span className="vr"></span>
                      <span className="d-flex align-items-center">
                        {moment.unix(contest.start_date).format("MMM D, YYYY")}
                      </span>
                    </div>
                  </Link>
                  {contest.short && (
                    <div className="contest-description-overlay">
                      <div
                        className="description-content"
                        dangerouslySetInnerHTML={{ __html: highlightHTMLText(contest.short, searchQuery) }}
                      />
                    </div>
                  )}
                </Col>
              ))
            ) : (
              <div className="text-center w-100">
                {searchQuery ? "No past contests match your search." : `No past contests available for ${selectedYear}.`}
              </div>
            )}
          </Row>
        </Col>
      </Row>

      {/* ===================== Stats Section ===================== */}
      <Row className="justify-content-center">
        <Col lg={10}>
          <div className="custom-separator my-5">
            <div className="separator-end"></div>
            <div className="separator-middle"></div>
            <div className="separator-end right"></div>
          </div>
          <h2 className="text-center mb-3">
            {statsRange === 'allTime' ? 'All-Time Statistics' : 'Past 3-Month Statistics'}
          </h2>
          <div className="d-flex justify-content-center mb-5">
            <Form.Check
              type="switch"
              id="stats-switch"
              label={statsRange === 'allTime' ? 'All Time' : '3-Months'}
              checked={statsRange === 'pastMonth'}
              onChange={() => setStatsRange(statsRange === 'allTime' ? 'pastMonth' : 'allTime')}
            />
          </div>
          {stats ? (
            <>
              {/* ===== Key Totals ===== */}
              <Row className="text-center mb-4">
                <Col xs={6} md={4} lg={2} className="mb-3">
                  <div className="contest-frame contest-frame-small">
                    <h4>{stats.total_contests}</h4>
                    <small className="text-muted">Contests</small>
                  </div>
                </Col>
                <Col xs={6} md={4} lg={2} className="mb-3">
                  <div className="contest-frame contest-frame-small">
                    <h4>{stats.total_submissions}</h4>
                    <small className="text-muted">Submissions</small>
                  </div>
                </Col>
                <Col xs={6} md={4} lg={2} className="mb-3">
                  <div className="contest-frame contest-frame-small">
                    <h4>{stats.total_votes}</h4>
                    <small className="text-muted">Votes</small>
                  </div>
                </Col>
                <Col xs={6} md={4} lg={2} className="mb-3">
                  <div className="contest-frame contest-frame-small">
                    <h4>{stats.unique_submitters}</h4>
                    <small className="text-muted">Participants</small>
                  </div>
                </Col>
                <Col xs={6} md={4} lg={2} className="mb-3">
                  <div className="contest-frame contest-frame-small">
                    <h4>{stats.unique_voters}</h4>
                    <small className="text-muted">Voters</small>
                  </div>
                </Col>
                <Col xs={6} md={4} lg={2} className="mb-3">
                  <div className="contest-frame contest-frame-small">
                    <h4>~{stats.avg_submissions_per_contest}</h4>
                    <small className="text-muted">Subs/Contest</small>
                  </div>
                </Col>
              </Row>

              {/* ===== Top Users ===== */}
              <Row className="mb-4">
                <Col xs={12} md={4} className="mb-3">
                  <h5 className="mb-3 text-center">Top Participation</h5>
                  <ul className="list-group contest-frame">
                    {stats.top_submitters.map((user, idx) => (
                      <li
                        key={idx}
                        className={`list-group-item d-flex align-items-center justify-content-between${idx > 0 ? ' contest-li' : ' contest-li-fix'}`}
                        style={{ backgroundColor: 'unset', borderColor: 'transparent' }}
                      >
                        <Link
                          to={`/gallery/user/${user.user_id}`}
                          className="d-flex align-items-center text-decoration-none"
                        >
                          {user.profile_picture && (
                            <Image
                              src={user.profile_picture}
                              roundedCircle
                              width={24}
                              height={24}
                              className="me-2"
                            />
                          )}
                          <span className="text-dark">{user.username}</span>
                        </Link>
                        <Badge bg="primary" pill>
                          {user.submission_count}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </Col>
                <Col xs={12} md={4} className="mb-3">
                  <h5 className="mb-3 d-flex align-items-center justify-content-center">
                    Top Voters
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-voters">
                          A point is given for voting for at least 3 cards in a contest.
                        </Tooltip>
                      }
                    >
                      <span className="ms-2 text-muted" style={{ cursor: 'pointer' }}>
                        <FaQuestionCircle />
                      </span>
                    </OverlayTrigger>
                  </h5>
                  <ul className="list-group contest-frame">
                    {stats.top_voters.map((user, idx) => (
                      <li
                        key={idx}
                        className={`list-group-item d-flex align-items-center justify-content-between${idx > 0 ? ' contest-li' : ' contest-li-fix'}`}
                        style={{ backgroundColor: 'unset', borderColor: 'transparent' }}
                      >
                        <Link
                          to={`/gallery/user/${user.user_id}`}
                          className="d-flex align-items-center text-decoration-none"
                        >
                          {user.profile_picture && (
                            <Image
                              src={user.profile_picture}
                              roundedCircle
                              width={24}
                              height={24}
                              className="me-2"
                            />
                          )}
                          <span className="text-dark">{user.username}</span>
                        </Link>
                        <Badge bg="primary" pill>
                          {user.points}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </Col>
                <Col xs={12} md={4}>
                  <h5 className="mb-3 d-flex align-items-center justify-content-center">
                    Top Performers
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-performers">
                          10 to 1 point(s) given for 1-10 place in a contest.
                        </Tooltip>
                      }
                    >
                      <span className="ms-2 text-muted" style={{ cursor: 'pointer' }}>
                        <FaQuestionCircle />
                      </span>
                    </OverlayTrigger>
                  </h5>
                  <ul className="list-group contest-frame">
                    {stats.best_performers.map((user, idx) => (
                      <li
                        key={idx}
                        className={`list-group-item d-flex align-items-center justify-content-between${idx > 0 ? ' contest-li' : ' contest-li-fix'}`}
                        style={{ backgroundColor: 'unset', borderColor: 'transparent' }}
                      >
                        <Link
                          to={`/gallery/user/${user.user_id}`}
                          className="d-flex align-items-center text-decoration-none"
                        >
                          {user.profile_picture && (
                            <Image
                              src={user.profile_picture}
                              roundedCircle
                              width={24}
                              height={24}
                              className="me-2"
                            />
                          )}
                          <span className="text-dark">{user.username}</span>
                        </Link>
                        <Badge bg="success" pill>
                          {user.points}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </Col>
              </Row>

              {/* ===== START: Top Voted Cards Section ===== */}
              {stats.top_voted_cards && stats.top_voted_cards.length > 0 && (
                <>
                  <Row className="text-center mb-5">
                    <h4 className="mb-1">Most Voted Cards</h4>
                    <Row xs={2} md={3} lg={5} className="g-2 justify-content-center">
                      {stats.top_voted_cards.map((card) => (
                        <Col key={card.submission_id} className="mt-auto d-flex flex-column">
                          <Link
                            to={buildUrlWithParams({ ...Object.fromEntries(new URLSearchParams(window.location.search)), selectedCard: card.card_id })}
                            preventScrollReset={true}
                            style={{ textDecoration: 'none', zIndex: '2' }}
                          >
                            <img
                              src={`/cards/${card.card_filename}.webp`}
                              alt={card.cardname}
                              className="img-fluid"
                            />
                          </Link>
                          <div className="mt-1">
                            <small className="text-muted d-block" style={{ fontSize: '0.8em' }}>
                              {card.contest_title}
                            </small>
                          </div>
                          <Badge
                            bg="warning"
                            text="dark"
                            className="d-inline-flex align-items-center mt-2 align-self-center"
                          >
                            <FaStar className="me-1" /> {card.vote_count} Votes
                          </Badge>
                        </Col>
                      ))}
                    </Row>
                  </Row>
                </>
              )}
              {/* ===== END: Top Voted Cards Section ===== */}
            </>
          ) : (
            <div className="text-center">Loading statistics...</div>
          )}
        </Col>
      </Row>
    </Container>
  );
}